import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { API_BASE_URL } from '../http/api-base-url.token';
import { AuthService } from './auth.service';

function isApiRequest(requestUrl: string, apiBaseUrl: string): boolean {
  const requestOrigin = new URL(requestUrl, location.origin).origin;
  const apiOrigin = new URL(apiBaseUrl, location.origin).origin;
  return requestOrigin === apiOrigin;
}

// Endpoints a caller can hit while deliberately unauthenticated. A 401 from one of these is an
// expected outcome of the call itself (bad credentials, unknown/expired token, ...), not a sign
// that the caller's own session has expired - it must reach the calling screen, not the global
// sign-out (BUG-D1-1). Paths are relative to apiBaseUrl, matching how AuthService builds them via
// joinUrl(), so this works whether or not apiBaseUrl itself includes an `/api` segment.
const UNAUTHENTICATED_AUTH_PATHS = [
  'auth/login',
  'auth/register',
  'auth/password-reset-request',
  'auth/password-reset-confirm',
  'auth/verify-email',
  'auth/resend-verification',
];

function isUnauthenticatedAuthRequest(requestUrl: string, apiBaseUrl: string): boolean {
  const apiPath = new URL(apiBaseUrl, location.origin).pathname.replace(/\/+$/, '');
  const requestPath = new URL(requestUrl, location.origin).pathname;
  return UNAUTHENTICATED_AUTH_PATHS.some(path => requestPath === `${apiPath}/${path}`);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const apiBaseUrl = inject(API_BASE_URL);
  const targetsApi = isApiRequest(req.url, apiBaseUrl);

  const token = auth.token();
  const authReq = token && targetsApi ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      // Only logout on a genuine 401 from the API itself. Network errors (status 0),
      // CORS failures, 404s, 500s, any error from a non-API origin, and a 401 from an
      // unauthenticated auth endpoint rejecting the call itself must NOT log the user out.
      if (
        targetsApi &&
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !isUnauthenticatedAuthRequest(req.url, apiBaseUrl)
      ) {
        auth.logout();
      }
      return throwError(() => error);
    }),
  );
};
